import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { IActivityLog, IAccessRoleChangeRecord } from 'app/models/activity-log.model';
import { areaDisplayNameKey, BUILT_IN_ROLE_NAME_KEYS, parentArea } from 'app/models/permission.model';
import { IdentityFormatPipe, MomentPipe } from 'app/pipes/pipes';

/**
 * The area's own grant, else its nearest parent's in the same map; undefined when neither is stated
 * (shown as "none", as before, rather than as an access level)
 */
function resolved(grants: Record<string, string>, area: string): string | undefined {
  for (let key: string | null = area; key !== null; key = parentArea(key)) {
    if (grants[key]) return grants[key];
  }
  return undefined;
}

interface GrantChange {
  area: string;
  nameKey: string;
  oldValue?: string;
  newValue?: string;
}

/**
 * Activity details of a role change, a staff member's role assignment or individual permissions:
 * the name change and every area whose access changed.
 */
@Component({
  selector: 'app-activity-access-role-details',
  imports: [CommonModule, TranslateModule, RouterModule, IdentityFormatPipe, MomentPipe],
  templateUrl: './activity-access-role-details.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  styleUrls: ['../activity-details-dialog/activity-details-content-styles.css']
})
export class ActivityAccessRoleDetailsComponent implements OnChanges {
  @Output() closeDialog = new EventEmitter<void>();
  @Input() log: IActivityLog;

  changes: IAccessRoleChangeRecord | null = null;
  grantChanges: GrantChange[] = [];
  nameChanged = false;

  ngOnChanges(): void {
    this.changes = this.log?.details?.accessRoleChanges ?? null;
    this.nameChanged = !!this.changes && (this.changes.oldName ?? null) !== (this.changes.newName ?? null);

    const oldGrants = this.changes?.oldGrants ?? {};
    const newGrants = this.changes?.newGrants ?? {};
    const areas = Array.from(new Set([...Object.keys(oldGrants), ...Object.keys(newGrants)])).sort();
    // Compare what each area resolves to: a sub-area without its own grant follows its parent, so removing
    // "Devices.Firmware: None" under "Devices: Read" is None → Read, not None → None
    this.grantChanges = areas
      .map(area => ({ area, nameKey: areaDisplayNameKey(area), oldValue: resolved(oldGrants, area), newValue: resolved(newGrants, area) }))
      .filter(change => change.oldValue !== change.newValue);
  }

  /** Staff member events link to the staff member, role events to the role */
  get targetLink(): string[] {
    const id = this.log?.details?.targetId;
    return /^\d+$/.test(id ?? '') ? ['/roles', id] : ['/staff', id];
  }

  levelKey(level?: string): string {
    return level ? { None: 'ACCESS_NONE', Read: 'ACCESS_READ', ReadWrite: 'ACCESS_READWRITE' }[level] ?? level : 'NONE';
  }

  /** Built-in role names are stored by their internal name; show them translated */
  roleNameKey(name?: string): string {
    const levels: Record<string, number> = { User: 0, EMAOnly: 10, AnnaPerennaMainUser: 40, Editor: 50, MainUser: 100, CustomerAdmin: 101 };
    return name && levels[name] !== undefined ? BUILT_IN_ROLE_NAME_KEYS[levels[name]] : name;
  }
}
